import { useMemo, useState, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import PropTypes from 'prop-types';
import { API_BASE } from '../../config/api.js';

const columns = [
  { id: 'qid', name: "QID", selector: row => row.QID, sortable: true },
  { name: "Callers ID", selector: row => row.CallersID },
  { name: "Contact Person", selector: row => row.ContactPerson },
  { name: "Status", selector: row => row.Status },
  { name: "Contact Number", selector: row => row.ContactNumber },
  { name: "Routed To Department", selector: row => row.RoutedToDepartment },
  { name: "Edited", selector: row => row.Edited },
  { name: "Main Category", selector: row => row.MainCat },
  { name: "Category", selector: row => row.Cat },
  { name: "Sub-Category", selector: row => row["Sub-Cat"] },
  { name: "Office", selector: row => row.Office },
  { name: "Created On", selector: row => row.CreatedOn },
  { name: "Modified On", selector: row => row.ModifiedOn },
  { name: "Closed On", selector: row => row.ClosedOn ?? "—" },
  { name: "History", selector: row => row.History },
  { name: "Notes", selector: row => row.Notes },
  { name: "Tags", selector: row => row.TAGS },
  { name: "Created By", selector: row => row.CreatedBy },
  { name: "Modified By", selector: row => row.ModifyiedBy },
  { name: "Routed On", selector: row => row.RoutedOn ?? "—" },
  { name: "Routed By", selector: row => row.RoutedBy ?? "—" }
];

const tableFilterConfig = [
  { key: 'mainCategory', label: 'Main Category' },
  { key: 'category', label: 'Category' },
  { key: 'subCategory', label: 'Sub Category' },
  { key: 'tag', label: 'Tag' },
];

const statusOptions = [
  '--Select--',
  'Initiated',
  'Closed',
  'Completed',
  'Refer',
  'Approved',
  'Corrected',
  'Advised',
];

const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;
const OVERDUE_STATUSES = ['Initiated', 'Refer'];

const isOverdue = (row) => {
  const status = String(row?.Status ?? '').trim();
  if (!OVERDUE_STATUSES.includes(status)) return false;
  const createdOn = row?.CreatedOn;
  if (!createdOn) return false;
  const created = new Date(createdOn);
  if (Number.isNaN(created.getTime())) return false;
  return (Date.now() - created.getTime()) >= FORTY_EIGHT_HOURS_MS;
};

const conditionalRowStyles = [
  {
    when: isOverdue,
    style: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
    },
  },
];

const pickFirst = (row, keys, fallback = '') => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) {
      return row[key];
    }
  }
  return fallback;
};

const normalizeListRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  const normalized = {
    ...row,
    QID: pickFirst(row, ['QID', 'QueryID', 'QueryId', 'queryid', 'qid', 'ID', 'id']),
    CallersID: pickFirst(row, ['CallersID', 'CallerID', 'callerid', 'callersid']),
    ContactPerson: pickFirst(row, ['ContactPerson', 'contactperson', 'name', 'Name']),
    Status: pickFirst(row, ['Status', 'status']),
    ContactNumber: pickFirst(row, ['ContactNumber', 'contactnumber', 'PhoneNo', 'phoneno', 'phone', 'Phone']),
    RoutedToDepartment: pickFirst(row, [
      'RoutedToDepartment',
      'routedtodepartment',
      'routeddepartment',
      'RoutedDepartment',
      'Department',
      'department'
    ]),
    Edited: pickFirst(row, ['Edited', 'edited']),
    MainCat: pickFirst(row, ['MainCat', 'maincat', 'MainCategory', 'maincategory']),
    Cat: pickFirst(row, ['Cat', 'cat', 'Category', 'category']),
    'Sub-Cat': pickFirst(row, ['Sub-Cat', 'subcat', 'SubCat', 'SubCategory', 'subcategory']),
    Office: pickFirst(row, ['Office', 'office']),
    CreatedOn: pickFirst(row, ['CreatedOn', 'createdon', 'CreatedDate', 'createddate', 'CreatedAt', 'createdat']),
    ModifiedOn: pickFirst(row, ['ModifiedOn', 'modifiedon', 'ModifiedDate', 'modifieddate', 'ModifiedAt', 'modifiedat']),
    ClosedOn: pickFirst(row, ['ClosedOn', 'closedon', 'ClosedDate', 'closeddate', 'ClosedAt', 'closedat']),
    History: pickFirst(row, ['History', 'history']),
    Notes: pickFirst(row, ['Notes', 'notes']),
    TAGS: pickFirst(row, ['TAGS', 'Tags', 'tags', 'Tag', 'tag']),
    CreatedBy: pickFirst(row, ['CreatedBy', 'createdby']),
    ModifyiedBy: pickFirst(row, ['ModifyiedBy', 'ModifiedBy', 'modifiedby', 'modifiedBy']),
    ModifiedBy: pickFirst(row, ['ModifiedBy', 'ModifyiedBy', 'modifiedby', 'modifiedBy']),
    RoutedOn: pickFirst(row, ['RoutedOn', 'routedon']),
    RoutedBy: pickFirst(row, ['RoutedBy', 'routedby'])
  };
  return normalized;
};

const normalizeListResponse = (payload) => {
  if (Array.isArray(payload)) return payload.map(normalizeListRow);
  if (Array.isArray(payload?.data)) return payload.data.map(normalizeListRow);
  if (Array.isArray(payload?.value)) return payload.value.map(normalizeListRow);
  if (Array.isArray(payload?.results)) return payload.results.map(normalizeListRow);
  if (Array.isArray(payload?.records)) return payload.records.map(normalizeListRow);
  if (Array.isArray(payload?.data?.data)) return payload.data.data.map(normalizeListRow);
  if (payload && typeof payload === 'object') return [normalizeListRow(payload)];
  return [];
};

const defaultFilters = {
  all: '',
  dateFrom: '',
  dateTo: '',
  cli: '',
  queryId: '',
  name: '',
  status: '',
  notEdited: false,
};

const defaultChecked = {
  all: false,
  dateFrom: false,
  dateTo: false,
  cli: false,
  queryId: false,
  name: false,
  status: false,
  notEdited: false,
};

const defaultTableFilters = {
  mainCategory: '',
  category: '',
  subCategory: '',
  tag: '',
};

const defaultTableChecked = {
  mainCategory: false,
  category: false,
  subCategory: false,
  tag: false,
};

const readCallerListState = (storageKey) => {
  let stored = {};

  try {
    stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
  } catch {
    stored = {};
  }

  const storedChecked = stored?.checked && typeof stored.checked === 'object' ? stored.checked : {};
  const storedTableChecked = stored?.tableChecked && typeof stored.tableChecked === 'object'
    ? stored.tableChecked
    : {};
  const storedPagination = stored?.pagination && typeof stored.pagination === 'object'
    ? stored.pagination
    : {};
  const storedSort = stored?.sort && typeof stored.sort === 'object' ? stored.sort : {};

  return {
    filters: {
      ...defaultFilters,
      ...(stored?.filters && typeof stored.filters === 'object' ? stored.filters : {}),
    },
    checked: Object.keys(defaultChecked).reduce((result, key) => ({
      ...result,
      [key]: storedChecked[key] === true,
    }), {}),
    tableFilters: {
      ...defaultTableFilters,
      ...(stored?.tableFilters && typeof stored.tableFilters === 'object' ? stored.tableFilters : {}),
    },
    tableChecked: Object.keys(defaultTableChecked).reduce((result, key) => ({
      ...result,
      [key]: storedTableChecked[key] === true,
    }), {}),
    results: Array.isArray(stored?.results) ? stored.results.map(normalizeListRow) : [],
    pagination: {
      page: Number(storedPagination.page) > 0 ? Number(storedPagination.page) : 1,
      rowsPerPage: Number(storedPagination.rowsPerPage) > 0
        ? Number(storedPagination.rowsPerPage)
        : 10,
    },
    sort: {
      columnId: storedSort.columnId === 'qid' ? 'qid' : '',
      direction: storedSort.direction === 'desc' ? 'desc' : 'asc',
    },
  };
};

const updateCallerListState = (storageKey, section, value) => {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
    localStorage.setItem(storageKey, JSON.stringify({
      ...(stored && typeof stored === 'object' ? stored : {}),
      [section]: value,
    }));
  } catch {
    localStorage.setItem(storageKey, JSON.stringify({ [section]: value }));
  }
};

export default function ListTab({ onRowClick, data, setData, storageUserId = '', reloadKey = 0 }) {
  const storageKey = `callerListState:${storageUserId}`;
  const initialState = useMemo(() => readCallerListState(storageKey), [storageKey]);
  const [filters, setFilters] = useState(() => initialState.filters);
  const [checked, setChecked] = useState(() => initialState.checked);
  const [tableFilters, setTableFilters] = useState(() => initialState.tableFilters);
  const [tableChecked, setTableChecked] = useState(() => initialState.tableChecked);
  const [pagination, setPagination] = useState(() => initialState.pagination);
  const [sort, setSort] = useState(() => initialState.sort);

  // const [data, setData] = useState([]);

  const handleInputChange = (key, value) => {
    const updated = { ...filters, [key]: value };
    setFilters(updated);
    updateCallerListState(storageKey, 'filters', updated);
  };

  const handleCheckboxChange = (key, value) => {
    const updated = { ...checked, [key]: value };
    setChecked(updated);
    updateCallerListState(storageKey, 'checked', updated);
  };

  const handleTableFilterChange = (key, value) => {
    const updated = { ...tableFilters, [key]: value };
    setTableFilters(updated);
    updateCallerListState(storageKey, 'tableFilters', updated);
  };

  const handleTableCheckboxChange = (key, value) => {
    const updated = { ...tableChecked, [key]: value };
    setTableChecked(updated);
    updateCallerListState(storageKey, 'tableChecked', updated);
  };

  useEffect(() => {
    setData(initialState.results);
  }, [initialState.results, setData]);

  const tableFilterOptions = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const buildOptions = (valueKey) => {
      const unique = new Set();

      rows.forEach((row) => {
        const rawValue = row?.[valueKey];
        if (rawValue === null || rawValue === undefined) {
          return;
        }

        const textValue = String(rawValue).trim();
        if (textValue) {
          unique.add(textValue);
        }
      });

      return Array.from(unique).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
    };

    return {
      mainCategory: buildOptions('MainCat'),
      category: buildOptions('Cat'),
      subCategory: buildOptions('Sub-Cat'),
      tag: buildOptions('TAGS'),
    };
  }, [data]);

  const filteredData = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];

    return rows.filter((row) => {
      if (tableChecked.mainCategory && tableFilters.mainCategory) {
        if (String(row?.MainCat ?? '').trim() !== tableFilters.mainCategory) {
          return false;
        }
      }

      if (tableChecked.category && tableFilters.category) {
        if (String(row?.Cat ?? '').trim() !== tableFilters.category) {
          return false;
        }
      }

      if (tableChecked.subCategory && tableFilters.subCategory) {
        if (String(row?.['Sub-Cat'] ?? '').trim() !== tableFilters.subCategory) {
          return false;
        }
      }

      if (tableChecked.tag && tableFilters.tag) {
        if (String(row?.TAGS ?? '').trim() !== tableFilters.tag) {
          return false;
        }
      }

      return true;
    });
  }, [data, tableChecked, tableFilters]);

  const handleSubmit = async () => {
    const params = {};
    const today = new Date();
    const isFutureDate = (value) => {
      if (!value) return false;
      const picked = new Date(value);
      if (Number.isNaN(picked.getTime())) return false;
      const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      return picked.getTime() > endOfToday.getTime();
    };

    if (checked.dateFrom && isFutureDate(filters.dateFrom)) {
      alert("Date can't be greater than Today !");
      return;
    }
    if (checked.dateTo && isFutureDate(filters.dateTo)) {
      alert("Date can't be greater than Today !");
      return;
    }

    const lsUser = (() => {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        return {};
      }
    })();
    const departmentName =
      lsUser.DepartmentName || lsUser.departmentName || lsUser.Department || lsUser.department || '';
    const createdBy =
      lsUser.FullName ||
      lsUser.fullName ||
      lsUser.fullname ||
      lsUser.UserName ||
      lsUser.username ||
      lsUser.loginName ||
      lsUser.LoginName ||
      '';
    const roleName =
      lsUser.RoleName || lsUser.roleName || 'FO';

    if (departmentName) {
      params.RoutedToDepartment = departmentName;
    }
    params.RoleName = roleName;

    if (!checked.all && createdBy) {
      params.CreatedBy = createdBy;
    }
    if (checked.all) {
      params.all = true;
    }

    if (checked.cli && filters.cli.trim()) params.cli = filters.cli.trim();
    if (checked.queryId && filters.queryId.trim()) params.queryId = filters.queryId.trim();
    if (checked.name && filters.name.trim()) params.name = filters.name.trim();
    if (checked.status && filters.status.trim()) params.status = filters.status.trim();

    if (checked.dateFrom && filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (checked.dateTo && filters.dateTo) params.dateTo = filters.dateTo;

    if (checked.notEdited) {
      params.notEdited = true;
    }

    if (tableChecked.mainCategory && tableFilters.mainCategory) {
      params.mainCategory = tableFilters.mainCategory;
    }
    if (tableChecked.category && tableFilters.category) {
      params.category = tableFilters.category;
    }
    if (tableChecked.subCategory && tableFilters.subCategory) {
      params.subCategory = tableFilters.subCategory;
    }
    if (tableChecked.tag && tableFilters.tag) {
      params.tag = tableFilters.tag;
    }

    try {
      const response = await fetch(`${API_BASE}/search-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Search failed (${response.status}): ${text}`);
      }

      const data = await response.json();
      const rows = normalizeListResponse(data);
      setData(rows);
      updateCallerListState(storageKey, 'results', rows);
    } catch (error) {
      console.error('API Error:', error);
    }
  };

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const previousReloadKeyRef = useRef(reloadKey);

  useEffect(() => {
    if (reloadKey !== previousReloadKeyRef.current) {
      previousReloadKeyRef.current = reloadKey;
      void handleSubmitRef.current();
    }
  }, [reloadKey]);

  const handleFormSubmit = (event) => {
    event.preventDefault();
    void handleSubmit();
  };


  return (
      <div className="card p-3 mt-3">
        <form onSubmit={handleFormSubmit}>
          <div className="row g-2 align-items-center mb-3">
            {/* <div className="col-12 col-lg-auto">
              <h5 className="mb-0">List</h5>
            </div> */}
            <div className="col-12 col-lg">
              <div className="row g-2 align-items-center">
                {tableFilterConfig.map(({ key, label }) => (
                  <div className="col-12 col-md-6 col-xl-3" key={key}>
                    <div className="d-flex align-items-center">
                      <label
                        className="form-label mb-0 me-2 text-nowrap small"
                        htmlFor={`tbl-${key}`}
                        style={{ minWidth: '90px' }}
                      >
                        {label}
                      </label>
                      <input
                        type="checkbox"
                        className="form-check-input me-2"
                        id={`tbl-chk-${key}`}
                        checked={tableChecked[key]}
                        onChange={(event) => handleTableCheckboxChange(key, event.target.checked)}
                        aria-label={`Enable ${label} filter`}
                      />
                      <select
                        id={`tbl-${key}`}
                        className="form-select flex-grow-1"
                        value={tableFilters[key]}
                        onChange={(e) => handleTableFilterChange(key, e.target.value)}
                      >
                        <option value="">--Select--</option>
                        {tableFilterOptions[key].map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <div className="card p-3">
                <h6 className="mb-3">Filter Options</h6>
                {[
                  { label: 'All', key: 'all', type: 'text', placeholder: 'Enter All' },
                  { label: 'Date From', key: 'dateFrom', type: 'date' },
                  { label: 'Date To', key: 'dateTo', type: 'date' },
                  { label: 'CLI', key: 'cli', type: 'text', placeholder: 'Enter CLI' },
                  { label: 'Query ID', key: 'queryId', type: 'text', placeholder: 'Enter Query ID' },
                  { label: 'Caller Name', key: 'name', type: 'text', placeholder: 'Enter Caller Name' },
                  { label: 'Status', key: 'status', type: 'select' },
                ].map(({ label, key, type, placeholder }) => (
                    <div className="d-flex align-items-center mb-2" key={key}>
                      <input
                          type="checkbox"
                          className="form-check-input me-2"
                          id={`chk-${key}`}
                          checked={checked[key]}
                          onChange={(event) => handleCheckboxChange(key, event.target.checked)}
                      />
                      <label htmlFor={`chk-${key}`} className="form-check-label me-2" style={{ minWidth: '100px' }}>{label}</label>
                      {type === 'select' ? (
                        <select
                          className="form-select form-select-sm"
                          value={filters[key]}
                          onChange={(e) => handleInputChange(key, e.target.value)}
                        >
                          {statusOptions.map((option) => {
                            const value = option === '--Select--' ? '' : option;
                            return (
                              <option key={option} value={value}>
                                {option}
                              </option>
                            );
                          })}
                        </select>
                      ) : (
                        <input
                            type={type}
                            className="form-control form-control-sm"
                            placeholder={placeholder || ''}
                            value={filters[key]}
                            onChange={(e) => handleInputChange(key, e.target.value)}
                        />
                      )}
                    </div>
                ))}

                <div className="form-check mb-3">
                  <input
                      type="checkbox"
                      className="form-check-input"
                      id="chk-notEdited"
                      checked={checked.notEdited}
                      onChange={(event) => handleCheckboxChange('notEdited', event.target.checked)}
                  />
                  <label htmlFor="chk-notEdited" className="form-check-label">Not Edited</label>
                </div>

                <button className="btn btn-primary w-100" type="submit">Show</button>
              </div>
            </div>

            <div className="col-md-8">
              <div className="card p-3">
                <h6 className="mb-3">Caller Table</h6>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    onRowClicked={onRowClick}
                    onRowDoubleClicked={onRowClick}
                    conditionalRowStyles={conditionalRowStyles}
                    pagination
                    paginationDefaultPage={pagination.page}
                    paginationPerPage={pagination.rowsPerPage}
                    onChangePage={(page) => {
                      const updated = { ...pagination, page };
                      setPagination(updated);
                      updateCallerListState(storageKey, 'pagination', updated);
                    }}
                    onChangeRowsPerPage={(rowsPerPage, page) => {
                      const updated = { page, rowsPerPage };
                      setPagination(updated);
                      updateCallerListState(storageKey, 'pagination', updated);
                    }}
                    defaultSortFieldId={sort.columnId || undefined}
                    defaultSortAsc={sort.direction !== 'desc'}
                    onSort={(column, direction) => {
                      const updated = { columnId: column?.id || '', direction };
                      setSort(updated);
                      updateCallerListState(storageKey, 'sort', updated);
                    }}
                    responsive
                    highlightOnHover
                    dense
                />
              </div>
            </div>
          </div>
        </form>
      </div>
  );
}

ListTab.propTypes = {
  onRowClick: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  setData: PropTypes.func.isRequired,
  storageUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reloadKey: PropTypes.number,
};
